import { useCallback } from 'react';
import { useParams } from 'react-router';
import { EmployeeService, type Employee } from 'src/api/EmployeeService';
import { invalidateEmployee, invalidateEmployees } from '@stores/employeeStore';

type SaveEmployee = (
    employee: Employee,
    employeeId?: string,
) => Promise<Employee | undefined>;

/*
 * Creates (no employeeId) or updates an employee and invalidates the
 * cached employee data on success. Unlike the useApiCall-based mutation
 * hooks (e.g. useDeleteEmployee), this exposes an async function that
 * returns the saved employee: legacy save flows continue with the
 * response — the new id feeds the ext-id step — which useApiCall's void
 * requestApiCall cannot provide.
 *
 * Error semantics: dynoapeAPI's post/put swallow HTTP errors and resolve
 * undefined (the axios interceptor has already shown the error toast),
 * so the falsy guard below skips the invalidation on a failed save;
 * only a missing departmentId throws.
 */
const useSaveEmployee = (): { saveEmployee: SaveEmployee } => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const saveEmployee = useCallback<SaveEmployee>(
        async (
            employee: Employee,
            employeeId?: string,
        ): Promise<Employee | undefined> => {
            if (!departmentId) {
                throw new Error(
                    'useSaveEmployee requires a departmentId route param',
                );
            }

            const savedEmployee = employeeId
                ? await EmployeeService.updateEmployee(
                      departmentId,
                      employeeId,
                      employee,
                  )
                : await EmployeeService.createEmployee(departmentId, employee);

            if (!savedEmployee) {
                return undefined;
            }

            invalidateEmployees(departmentId);

            if (employeeId) {
                invalidateEmployee(departmentId, employeeId);
            }

            return savedEmployee;
        },
        [departmentId],
    );

    return { saveEmployee };
};

export default useSaveEmployee;
